import { useEffect, useState } from "react";
import {
  type OrphanAuditReport,
  fetchOrphanAuditReport,
} from "../../api/admin-storage-orphan-audit";
import { PageHeader } from "../../components/PageHeader";
import { ErrorBanner, LoadingIndicator } from "../../components/TableParts";
import { t } from "../../i18n";
import { HistoryTable } from "./storage-orphan-audit/HistoryTable";
import { LatestRunCard } from "./storage-orphan-audit/LatestRunCard";
import { PrefixBreakdownTable } from "./storage-orphan-audit/PrefixBreakdownTable";
import { auditStyles as styles } from "./storage-orphan-audit/storage-orphan-audit-helpers";

// V1.1 Task 4 (2026-04-26) — Orphan audit (dry-run) report viewer.
// Read-only. NO delete button. Backend reads the
// `storage_lifecycle.orphan_audit_dry_run` AE event emitted weekly by
// `apps/cron/src/cron-storage-lifecycle.ts`. Sub-components live under
// `./storage-orphan-audit/` so this file stays a thin orchestrator.

export function StorageOrphanAuditPage(): React.JSX.Element {
  const [report, setReport] = useState<OrphanAuditReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchOrphanAuditReport();
        if (!cancelled) setReport(data);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : t("common.failed_to_load"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return (): void => {
      cancelled = true;
    };
  }, []);

  return (
    <div>
      <PageHeader
        title={t("storage_orphan_audit.title")}
        description={t("storage_orphan_audit.description")}
      />
      <DryRunBanner />
      {error && <ErrorBanner message={error} />}
      {loading ? (
        <LoadingIndicator />
      ) : !report || report.latest_run === null ? (
        <p style={styles.emptyText}>{t("storage_orphan_audit.no_runs_yet")}</p>
      ) : (
        <>
          <LatestRunCard run={report.latest_run} />
          <PrefixBreakdownTable entries={report.latest_run.prefix_breakdown} />
          <HistoryTable entries={report.history} />
        </>
      )}
    </div>
  );
}

// ─── Local sub-component ───

function DryRunBanner(): React.JSX.Element {
  return (
    <div style={styles.banner} role="note">
      {t("storage_orphan_audit.banner_dry_run")}
    </div>
  );
}
